<template>
  <v-navigation-drawer
    v-model="drawerVisible"
    app
    left
    :temporary="false"
    :permanent="true"
    :floating="true"
    :width="drawerVisible ? (mini ? 65 : 250) : 0"
    class="job-updates-feed modern-feed"
    :class="vDrawerClasses"
    :style="drawerStyles"
  >
  <div class="drawer-content">
    <template>
      <div class="modern-drawer-header">
        <div class="header-controls">
          <button class="modern-button glass-button" @click="toggleMini">
            <span class="button-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="icon-animate">
                <path d="M13 5l7 7-7 7M5 5l7 7-7 7" v-if="mini && !ragContentVisible"/>
                <path d="M19 5l-7 7 7 7M11 5l-7 7 7 7" v-else-if="!mini && !ragContentVisible"/>
                <path d="M11 5l-7 7 7 7M19 5l-7 7 7 7" v-else-if="mini && ragContentVisible"/>
                <path d="M5 5l7 7-7 7M13 5l7 7-7 7" v-else/>
              </svg>
            </span>
            <span class="button-text" v-if="!mini">
              {{ ragContentVisible ? 'Collapse Sidebar' : 'Collapse Sidebar' }}
            </span>
          </button>

          <button v-if="!mini" class="modern-button glass-button" @click="handleRefresh" :class="{ 'refreshing': isRefreshing }">
            <span class="button-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" :class="{ 'animate-spin': isRefreshing }">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 12c0-2.8 1.2-5.2 3.2-7C7 3.2 9.6 2 12.5 2M22 12c0 2.8-1.2 5.2-3.2 7-1.8 1.8-4.4 3-7.3 3"/>
              </svg>
            </span>
            <span class="button-text">Refresh Sources</span>
          </button>
        </div>

        <!-- Projects Section. -->
        <div class="projects-container" v-if="projects.length > 0">
          <h3 class="section-title modern-title" v-if="!mini">Planets</h3>
          <div class="projects-list" :class="{ 'mini-mode': mini }">
            <div
              v-for="project in projects"
              :key="`project-${project.id}`"
              @click="handleProjectClick(project)"
              class="project-card modern-card"
              :class="{
                'mini-mode': mini,
                'active': projectId === project.id
              }"
              :style="{ '--project-color': getProjectColor(project) }"
            >
              <v-tooltip right :disabled="false" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <div class="project-card-content" v-bind="attrs" v-on="on">
                    <div class="project-icon-wrapper">
                      <v-icon dark small>mdi-earth</v-icon>
                    </div>
                    <div v-if="!mini" class="project-details">
                      <div class="project-name">{{ project.name }}</div>
                      <div class="project-id">ID: {{ project.id }}</div>
                      <div class="project-description" v-if="project.description">
                        {{ truncateText(project.description, 40) }}
                      </div>
                    </div>
                  </div>
                </template>
                <v-card class="planet-tooltip modern-tooltip pa-3">
                  <div class="d-flex align-center mb-2">
                    <v-avatar :color="getProjectColor(project)" size="24" rounded="sm" class="mr-2">
                      <v-icon dark small>mdi-earth</v-icon>
                    </v-avatar>
                    <span class="font-weight-medium">{{ project.name }}</span>
                  </div>
                  <div class="project-id mb-2">
                    <v-icon x-small dark class="mr-1">mdi-tag-outline</v-icon>
                    ID: {{ project.id }}
                  </div>

                  <div class="tooltip-details">
                    <div class="detail-row mb-2">
                      <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                      <span>{{ getProjectFileCount(project.id) }} associated files</span>
                    </div>

                    <div class="file-list-container">
                      <div v-for="(file, index) in getProjectFiles(project.id)"
                           :key="index"
                           class="file-item">
                        <v-icon x-small class="mr-1">{{ getFileIcon(file) }}</v-icon>
                        <span class="file-name">{{ file.file_name || file.name || file.video_title }}</span>
                      </div>
                      <div v-if="getProjectFiles(project.id).length === 0" class="no-files">
                        No sources/files associated with this planet yet
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-tooltip>
            </div>
          </div>
        </div>

        <div class="metrics-container">
          <!-- Sources Metric -->
          <v-tooltip bottom max-width="200">
            <template v-slot:activator="{ on, attrs }">
              <div
                class="metric-card modern-metric"
                :class="{ 'mini-mode': mini }"
                @click="handleSubmissionsIconClick"
                style="cursor: pointer"
                v-bind="attrs"
                v-on="on"
              >
                <div class="metric-content">
                  <div class="metric-value">
                    <span class="number metric-number">
                      <span v-if="hasProcessingJobs" class="spinner modern-spinner"></span>
                      <template v-else>{{ sortedJobs.length }}</template>
                    </span>
                    <span v-if="!mini" class="label">Sources</span>
                  </div>
                </div>
              </div>
            </template>
            <span>View all Uploads and Sources (for the Current Project/Galaxy)</span>
          </v-tooltip>

          <!-- New Jobs Metric -->
          <v-tooltip v-if="newJobsCount > 0" bottom max-width="200">
            <template v-slot:activator="{ on, attrs }">
              <div
                class="metric-card modern-metric highlight"
                :class="{ 'mini-mode': mini }"
                v-bind="attrs"
                v-on="on"
              >
                <div class="metric-content">
                  <div class="metric-value">
                    <span class="number metric-number success">+{{ newJobsCount }}</span>
                    <span v-if="!mini" class="label">New Jobs</span>
                  </div>
                </div>
              </div>
            </template>
            <span>Recently added processing jobs (for the Current Project/Galaxy)</span>
          </v-tooltip>
        </div>

        <!-- Empty State -->
        <v-list-item v-if="sortedJobs.length === 0 && !mini" class="empty-state-jobs modern-empty">
          <v-list-item-content>
            <div class="empty-state-jobs-content">
              <div class="icon-container modern-icon-container">
                <v-icon size="48" class="empty-icon modern-empty-icon">mdi-cloud-question</v-icon>
              </div>
              <div v-if="!mini" class="empty-message">
                <h3 class="empty-title">No Recent Sources</h3>
                <p class="empty-description">
                  Build:
                  <ul class="action-list">
                    <li>Upload files</li>
                    <li>Upload images</li>
                    <li>Paste text</li>
                    <li>Paste YouTube links</li>
                  </ul>
                </p>
              </div>
              <template v-if="!this.user.is_pro && !currentUser.loggedIn">
                <v-btn
                  color="primary"
                  :loading="subscriptionLoading"
                  @click="navigateToEmailLogin"
                  class="mt-4 modern-pro-button"
                  small
                  rounded
                >
                  <v-icon small left>mdi-star-three-points-outline</v-icon>
                  <span>Login To Try Pro</span>
                </v-btn>
              </template>
              <template v-else-if="!this.user.is_pro && currentUser.loggedIn">
                <v-btn
                  color="primary"
                  :loading="subscriptionLoading"
                  @click="upgradePlan('pro')"
                  class="mt-4 modern-pro-button"
                  small
                  rounded
                >
                  <v-icon small left>mdi-star-three-points-outline</v-icon>
                  Try Pro
                </v-btn>
              </template>
            </div>
          </v-list-item-content>
        </v-list-item>
        <template v-else>
          <v-list-item
            v-for="(job) in sortedJobs"
            :key="`job-${job.type}-${job.id}`"
            @click="handleJobClick(job)"
            class="job-item modern-job-item"
            :data-job-type="job.type"
            :data-is-image="isImageFile(job)"
          >
          <v-tooltip right :disabled="!mini" max-width="400">
              <template v-slot:activator="{ on, attrs }">
                <div class="job-item-content" v-bind="attrs" v-on="on">
                  <v-list-item-icon>
                    <v-avatar
                      size="48"
                      rounded="sm"
                      class="outer-avatar modern-avatar"
                      :style="{ background: getJobColor(job) }"
                    >
                      <v-avatar
                        size="32"
                        rounded="sm"
                        class="inner-avatar"
                      >
                        <v-icon dark small>{{ getJobIcon(job) }}</v-icon>
                      </v-avatar>
                    </v-avatar>
                  </v-list-item-icon>

                  <v-list-item-content v-if="!mini">
                    <v-list-item-title class="job-title text-caption">
                      {{ getJobName(job) }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="job-subtitle text-caption">
                      {{ getJobTypeDisplay(job) }} • {{ getLastAttemptedAt(job) | formatRelativeDate }}
                    </v-list-item-subtitle>
                    <v-chip
                      v-if="job.sensitivity_level"
                      x-small
                      :color="getSensitivityColor(job.sensitivity_level)"
                      text-color="white"
                      class="mt-1 modern-chip"
                    >
                      <v-icon left x-small>{{ getSensitivityIcon(job.sensitivity_level) }}</v-icon>
                      {{ job.sensitivity_level }}
                    </v-chip>
                  </v-list-item-content>
                </div>
              </template>

              <!-- Tooltip Content for Mini Mode -->
              <v-card class="job-tooltip modern-tooltip pa-3">
                <div class="d-flex align-center mb-2">
                  <v-avatar :color="getJobColor(job)" size="24" rounded="sm" class="mr-2">
                      <v-icon dark small>{{ getJobIcon(job) }}</v-icon>
                    </v-avatar>
                  <span class="font-weight-medium">{{ getJobName(job) }}</span>
                </div>

                <div class="tooltip-details">
                  <div class="detail-row">
                    <v-icon x-small color="grey">mdi-tag-outline</v-icon>
                    <span>{{ getJobTypeDisplay(job) }}</span>
                  </div>
                  <div class="detail-row">
                    <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                    <span>{{ getLastAttemptedAt(job) | formatRelativeDate }}</span>
                  </div>
                  <div class="detail-row">
                    <v-icon x-small color="grey">mdi-information-outline</v-icon>
                    <span>{{ getJobStatus(job) }}</span>
                  </div>
                  <div v-if="job.sensitivity_level && user.is_pro_team" class="detail-row">
                    <v-icon x-small color="grey">mdi-lock</v-icon>
                    <span>{{ job.sensitivity_level }}</span>
                  </div>
                </div>
              </v-card>
            </v-tooltip>
            <v-list-item-action v-if="!mini">
              <div class="status-indicator modern-status d-flex align-center">
                <template v-if="job.type === 'Chat Message'">
                  <v-progress-circular
                    v-if="job.web_crawl_status === 'processing' || job.web_crawl_status === 'indexing'"
                    indeterminate
                    :color="getStatusColor(job.web_crawl_status)"
                    size="16"
                    width="2"
                    class="mr-1"
                  ></v-progress-circular>
                  <v-icon v-else :color="getStatusColor(job.web_crawl_status)" x-small class="mr-1">
                    {{ getStatusIcon(job.web_crawl_status) }}
                  </v-icon>
                  <v-progress-circular
                    v-if="job.index_build_status === 'processing' || job.index_build_status === 'indexing'"
                    indeterminate
                    :color="getStatusColor(job.index_build_status)"
                    size="16"
                    width="2"
                    class="mr-1"
                  ></v-progress-circular>
                  <v-icon v-else :color="getStatusColor(job.index_build_status)" x-small class="mr-1">
                    {{ getStatusIcon(job.index_build_status) }}
                  </v-icon>
                </template>
                <template v-else-if="job.type === 'Web Crawl'">
                  <v-progress-circular
                    v-if="job.status === 'processing' || job.status === 'indexing'"
                    indeterminate
                    :color="getStatusColor(job.status)"
                    size="16"
                    width="2"
                    class="mr-1"
                  ></v-progress-circular>
                  <v-icon v-else :color="getStatusColor(job.status)" x-small class="mr-1">
                    {{ getStatusIcon(job.status) }}
                  </v-icon>
                </template>
                <template v-else>
                  <v-progress-circular
                    v-if="job.status === 'processing' || job.status === 'indexing'"
                    indeterminate
                    :color="getStatusColor(job.status)"
                    size="16"
                    width="2"
                    class="mr-1"
                  ></v-progress-circular>
                  <v-icon v-else :color="getStatusColor(job.status)" x-small class="mr-1">
                    {{ getStatusIcon(job.status) }}
                  </v-icon>
                  <v-progress-circular
                    v-if="job.index_build_status === 'processing' || job.index_build_status === 'indexing'"
                    indeterminate
                    :color="getStatusColor(job.index_build_status)"
                    size="16"
                    width="2"
                    class="mr-1"
                  ></v-progress-circular>
                  <v-icon v-else :color="getStatusColor(job.index_build_status)" x-small class="mr-1">
                    {{ getStatusIcon(job.index_build_status) }}
                  </v-icon>
                </template>
              </div>
            </v-list-item-action>
          </v-list-item>
        </template>
    </div>
    </template>
  </div>
  </v-navigation-drawer>
</template>

<script>
import jobUpdatesApi from '@/websocket/api';
import { min } from 'lodash';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'JobUpdatesFeed',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    ragContentVisible: {
      type: Boolean,
      default: false
    },
    projects: {
      type: Array,
      default: () => []
    },
    topOffset: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isRefreshing: false,
      mini: true,
      drawerVisible: this.visible,
      previousJobCount: 0,
      fileSubmissions: [],
      textSubmissions: [],
      youtubeTranscriptSubmissions: [],
      totalEntityCount: 0,
      showSparks: false,
      isCollapsed: false,
      jobs: [],
      pollingInterval: null,
      isPolling: false,
      noProcessingJobsCount: 0,
      maxNoProcessingPolls: 5, // Poll every 15 seconds, stop after 75 seconds.
      listItemHeight: 75,
      projectColors: {}
    }
  },
  computed: {
    ...mapState('websocket', ['tempMessage', 'showTempMessage']),
    ...mapState("auth", ["currentUser", "oauthLoading", "loading"]),
    ...mapState('websocket', ['subscriptionLoading']),
    projectFilesMap() {
      const map = {};
      // file submissions.
      this.fileSubmissions.forEach(file => {
        const projectId = file.eccs_project_id;
        if (!map[projectId]) {
          map[projectId] = [];
        }
        map[projectId].push(file);
      });
      // text submissions
      this.textSubmissions.forEach(text => {
        const projectId = text.eccs_project_id;
        if (!map[projectId]) {
          map[projectId] = [];
        }
        map[projectId].push(text);
      });
      // youtube transcript submissions
      this.youtubeTranscriptSubmissions.forEach(youtube => {
        const projectId = youtube.eccs_project_id;
        if (!map[projectId]) {
          map[projectId] = [];
        }
        map[projectId].push(youtube);
      });
      return map;
    },
    vDrawerClasses() {
      return {
        'rag-content-visible': this.ragContentVisible
      };
    },
    drawerStyles() {
      return {
        transform: this.drawerVisible ? 'translateX(0)' : 'translateX(100%)',
        top: `${this.topOffset}px`,
        height: `calc(100% - ${this.topOffset}px)`,
      };
    },
    hasProcessingJobs() {
      return this.sortedJobs.some(job => {
        return ['processing', 'indexing'].includes(job.status.toLowerCase()) ||
                ['processing', 'indexing'].includes((job.index_build_status || '').toLowerCase());
      });
    },
    processingJobCount() {
      return this.sortedJobs.filter(job => {
        return ['processing', 'indexing'].includes(job.status.toLowerCase()) ||
                ['processing', 'indexing'].includes((job.index_build_status || '').toLowerCase());
      }).length;
    },
    newJobsCount() {
      return Math.max(0, this.jobCount - this.previousJobCount);
    },
    sortedJobs() {
      const allJobs = [
        ...this.fileSubmissions.map(fs => ({
          ...fs,
          type: 'File Submission',
          updated_at: fs.updated_at || fs.created_at
        })),
        ...this.textSubmissions.map(ts => ({
          ...ts,
          type: 'Text Submission',
          updated_at: ts.updated_at || ts.created_at
        })),
        ...this.youtubeTranscriptSubmissions.map(yts => ({
          ...yts,
          type: 'YouTube Transcript',
          updated_at: yts.updated_at || yts.created_at
        })),
      ];

      // Sort all jobs by updated_at
      const sortedAllJobs = allJobs.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

      return sortedAllJobs;
    },
    jobCount() {
      return this.sortedJobs.length;
    }
  },
  mounted() {
    setTimeout(async () => {
      await this.fetchJobs();
    }, 3000);
    // Generate consistent colors for projects
    this.projects.forEach(project => {
      this.projectColors[project.id] = this.generateProjectColor(project.id);
    });
  },
  beforeDestroy() {
    this.stopPolling();
    this.previousJobCount = 0;
  },
  watch: {
    visible(newValue) {
      this.drawerVisible = newValue;
    },
    drawerVisible(newValue) {
      this.$emit('update:visible', newValue);
    },
    hasProcessingJobs(newValue) {
      this.$emit('processing-jobs-change', newValue);
    },
    processingJobCount(newValue) {
      this.$emit('processing-job-count-change', newValue);
    },
    projectId: {
      handler() {
        this.fetchJobs();
      },
      immediate: true
    },
    projects: {
      handler(newProjects) {
        // Generate colors for new projects
        newProjects.forEach(project => {
          if (!this.projectColors[project.id]) {
            this.projectColors[project.id] = this.generateProjectColor(project.id);
          }
        });
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('websocket', ['setActiveObject', 'showTempMessageFn','upgradePlan']),
    ...mapActions("auth", ["oauthLogin"]),
    lightenColor(color, percent) {
      const num = parseInt(color.slice(1), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = ((num >> 8) & 0x00FF) + amt;
      const B = (num & 0x0000FF) + amt;
      return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R > 255 ? 255 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G > 255 ? 255 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B > 255 ? 255 : B) : 255)).toString(16).slice(1)}`;
    },
    getFileIcon(file) {
      if (file.video_title) return 'mdi-youtube';
      if (file.name) return 'mdi-text-box-outline';

      if (!file.file_name) return 'mdi-file-outline';

      const fileName = file.file_name.toLowerCase();
      const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
      const docExts = ['.doc', '.docx', '.pdf', '.txt', '.md'];
      const spreadsheetExts = ['.xls', '.xlsx', '.csv'];
      const presentationExts = ['.ppt', '.pptx'];

      if (imageExts.some(ext => fileName.endsWith(ext))) {
        return 'mdi-file-image-outline';
      } else if (docExts.some(ext => fileName.endsWith(ext))) {
        return 'mdi-file-document-outline';
      } else if (spreadsheetExts.some(ext => fileName.endsWith(ext))) {
        return 'mdi-file-table-outline';
      } else if (presentationExts.some(ext => fileName.endsWith(ext))) {
        return 'mdi-file-presentation-box';
      }

      return 'mdi-file-outline';
    },

    // Get files for a specific project
    getProjectFiles(projectId) {
      return this.projectFilesMap[projectId] || [];
    },

    // Get file count for a specific project
    getProjectFileCount(projectId) {
      return (this.projectFilesMap[projectId] || []).length;
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    handleProjectClick(project) {
      // Emit the selected project to the parent component
      this.$emit('project-selected', project.id);
      // Set active object for the project
      this.setActiveObject({
        type: 'ECCSProject',
        id: project.id,
      });
      this.$emit('start-first-project');
    },
    generateProjectColor(id) {
      // Generate a consistent color based on the project ID
      const colors = [
        '#3B82F6', // blue
        '#10B981', // green
        '#F43F5E', // red
        '#8B5CF6', // purple
        '#F59E0B', // amber
        '#6366F1', // indigo
        '#EC4899', // pink
        '#14B8A6', // teal
        '#F97316', // orange
        '#8D4DAA'  // purple-pink
      ];

      // Use the project ID to pick a color deterministically
      const colorIndex = parseInt(String(id).slice(-2), 10) % colors.length;
      return colors[colorIndex];
    },
    getProjectColor(project) {
      // Get color from cache or generate a new one
      if (!this.projectColors[project.id]) {
        this.projectColors[project.id] = this.generateProjectColor(project.id);
      }
      // Return gradient instead of solid color
      const baseColor = this.projectColors[project.id];
      const lighterColor = this.lightenColor(baseColor, 20);
      return `linear-gradient(135deg, ${baseColor}, ${lighterColor})`;
    },
    // Add helper method to lighten colors
    isImageFile(job) {
      if (job.type !== 'File Submission') return false;
      const imageExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff', '.tif'];
      const fileName = job.file_name ? job.file_name.toLowerCase() : '';
      return imageExtensions.some(ext => fileName.endsWith(ext));
    },
    handleClick() {
      if (this.currentUser.loggedIn) {
        this.$router.push({ name: 'entities' });
      } else {
        this.showTempMessageFn({
          message: 'Please log in to view entities',
          type: 'error',
          timeout: 5000
        });
      }
    },
    handleSubmissionsIconClick() {
      this.$emit('handle-submissions-icon-click')
      this.settingsDialogOpen = false
    },
    async handleRefresh() {
      this.isRefreshing = true;
      await this.fetchJobs();
      setTimeout(() => {
        this.isRefreshing = false;
      }, 600);
    },
    toggleMini() {
      this.mini = !this.mini;
      this.$emit('mini-change', this.mini);
    },
    navigateToEmailLogin() {
      // Navigate to the login page
      this.$router.push({ name: 'BasicLogin' });
    },
    getSensitivityIcon(level) {
      switch(level) {
        case 'Internal':
          return 'mdi-lock-open-variant-outline';
        case 'Confidential':
          return 'mdi-lock';
        default:
          return 'mdi-eye-off';
      }
    },
    getSensitivityColor(level) {
      switch(level) {
        case 'Internal':
          return '#10B981';
        case 'Confidential':
          return '#F43F5E';
        default:
          return 'grey';
      }
    },
    shouldShowConnector(job, index) {
      return job.type === 'Chat Message' &&
             job.associated_web_crawls &&
             job.associated_web_crawls.length > 0;
    },
    getConnectorHeight(index) {
      let height = 0;
      let i = index + 1;
      while (i < this.sortedJobs.length && this.sortedJobs[i].type === 'Web Crawl') {
        height += this.listItemHeight;
        i++;
      }
      return `${height}px`;
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    getJobStatus(job) {
      if (job.type === 'Chat Message') {
        return `${job.web_crawl_status} / ${job.index_build_status || 'N/A'}`;
      } else if (job.type === 'Web Crawl') {
        return job.status || 'N/A';
      }
      return `${job.status} / ${job.index_build_status || 'N/A'}`;
    },
    getIndexBuildStatus(job) {
      if (job.type === 'Chat Message') {
        if (!job.thumbs_up) {
          return 'n/a';
        }
        return job.index_build_status || 'pending';
      }
      return job.index_build_status || 'n/a';
    },
    getJobColor(job) {
      switch(job.type) {
        case 'File Submission': {
          const imageExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff', '.tif'];
          const fileName = job.file_name ? job.file_name.toLowerCase() : '';
          const isImage = imageExtensions.some(ext => fileName.endsWith(ext));

          // More modern gradients for files
          return isImage ? 'linear-gradient(135deg, #10B981, #3B82F6)' : 'linear-gradient(135deg, #F43F5E, #EC4899)';
        }
        case 'Text Submission':
          return 'linear-gradient(135deg, #3B82F6, #06B6D4)'; // Vibrant blue to cyan
        case 'Web Crawl':
          return 'linear-gradient(135deg, #8B5CF6, #6366F1)'; // Purple to indigo
        case 'YouTube Transcript':
          return 'linear-gradient(135deg, #FF0000, #DC2626)'; // YouTube red gradient
        case 'Chat Message':
          return 'linear-gradient(135deg, #F59E0B, #EF4444)'; // Amber to red
        default:
          return 'linear-gradient(135deg, #64748B, #475569)'; // Modern slate
      }
    },
    getRandomColor() {
      const colors = [
        'indigo darken-2',
        'deep-purple darken-2',
        'blue darken-3',
        'teal darken-2',
        'green darken-3',
        'orange darken-3',
        'deep-orange darken-2',
        'brown darken-2',
        'blue-grey darken-2',
        'red darken-2',
        'pink darken-2'
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },
    getStatusColor(status) {
      switch(status.toLowerCase()) {
        case 'pending': return 'grey';
        case 'processing': return 'primary';
        case 'completed': return 'success';
        case 'failed': return 'error';
        case 'success': return 'success';
        case 'indexing': return 'info';
        case 'n/a': return 'grey';
        default: return 'grey';
      }
    },
    getStatusIcon(status) {
      switch(status.toLowerCase()) {
        case 'pending': return 'mdi-clock-outline';
        case 'processing': return 'mdi-cog';
        case 'completed': return 'mdi-check-circle-outline';
        case 'failed': return 'mdi-alert-circle-outline';
        case 'success': return 'mdi-check-circle-outline';
        case 'indexing': return 'mdi-database-sync';
        case 'n/a': return 'mdi-minus-circle-outline';
        default: return 'mdi-help-circle-outline';
      }
    },
    handleJobClick(job) {
      let type;
      switch(job.type) {
        case 'File Submission':
          type = 'FileSubmission';
          break;
        case 'Text Submission':
          type = 'TextSubmission';
          break;
        case 'Web Crawl':
          type = 'Website';
          break;
        case 'YouTube Transcript':
          type = 'YouTubeTranscriptSubmission';
          break;
        case 'Chat Message':
          type = 'ChatMessage';
          break;
        default:
          type = 'Unknown';
      }

      this.setActiveObject({
        type: type,
        id: job.id,
      });
      this.$emit('start-first-project');
    },
    // Fetches the latest job updates
    async fetchJobs() {
      try {
        const { data } = await jobUpdatesApi.getJobUpdates({
          limit: 100,
          time_range: 720,
          project_id: this.projectId
        });
        this.fileSubmissions = data.file_submissions;
        this.textSubmissions = data.text_submissions;
        this.youtubeTranscriptSubmissions = data.youtube_transcript_submissions;

        this.previousJobCount = this.jobCount;

        if (this.totalEntityCount !== data.total_entity_count) {
          this.previousEntityCount = this.totalEntityCount;
          this.totalEntityCount = data.total_entity_count;
          this.showSparks = true;
          setTimeout(() => {
            this.showSparks = false;
          }, 5000);
        }
        // Check if we should continue polling
        this.$nextTick(() => {
          this.$emit('update-job-count', this.jobCount);

          if (!this.hasProcessingJobs) {
            this.noProcessingJobsCount++;
            if (this.noProcessingJobsCount > this.maxNoProcessingPolls) {
              this.stopPolling();
              this.$emit('processing-jobs-change', false);
            }
          } else {
            this.noProcessingJobsCount = 0;
            this.$emit('processing-jobs-change', true);
          }
        });
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      }
    },
    startPolling() {
      if (!this.isPolling) {
        this.isPolling = true;
        this.noProcessingJobsCount = 0;
        this.fetchJobs(); // Fetch immediately when starting
        this.pollingInterval = setInterval(() => {
          this.fetchJobs();
        }, 5000); // Poll every 5 seconds
      }
    },
    stopPolling() {
      if (this.isPolling) {
        clearInterval(this.pollingInterval);
        this.isPolling = false;
        this.noProcessingJobsCount = 0;
      }
    },
    onNewJobSubmitted() {
      this.startPolling();
    },
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleString();
    },
    getJobName(job) {
      switch(job.type) {
        case 'File Submission':
          return job.file_name;
        case 'Text Submission':
          return job.name ? job.name.substring(0, 20) + '...' : 'N/A';
        case 'Web Crawl':
          return job.name || job.url;
        case 'YouTube Transcript':
          return job.video_title || `YouTube ${job.id}`;
        case 'Chat Message':
          return job.user_message.substring(0, 50) + (job.user_message.length > 20 ? '...' : '');
        default:
          return 'Unknown Job';
      }
    },
    getJobIcon(job) {
      switch(job.type) {
        case 'File Submission': {
          const imageExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff', '.tif'];
          const fileName = job.file_name ? job.file_name.toLowerCase() : '';
          const isImage = imageExtensions.some(ext => fileName.endsWith(ext));

          return isImage ? 'mdi-file-image-outline' : 'mdi-file-document-outline';
        }
        case 'Text Submission':
          return 'mdi-text-box-outline';
        case 'Web Crawl':
          return 'mdi-web';
        case 'YouTube Transcript':
          return 'mdi-youtube';
        case 'Chat Message':
          return 'mdi-web';
        default:
          return 'mdi-help-circle-outline';
      }
    },
    getJobTypeDisplay(job) {
      if (job.type === 'Web Crawl') {
        const url = new URL(job.url);
        const siteName = url.hostname.replace('www.', '');
        return `${siteName}`;
      }
      // Return only the first word of the job type
      return job.type.split(' ')[0];
    },
    getLastAttemptedAt(job) {
      if (job.type === 'Chat Message') {
        return job.web_crawl_last_attempted_at || job.index_build_last_attempted_at || new Date().toISOString();
      }
      return job.updated_at;
    },
  }
}
</script>

<style scoped>
/* Keep all original styles and add modern enhancements */
.job-updates-feed {
  height: 100%;
  font-family: 'Roboto', sans-serif;
  transition: all 0.3s ease;
  overflow: hidden;
}

.v-navigation-drawer.rag-content-visible {
  right: 0;
  left: auto;
  z-index: 102;
}

@media screen and (max-width: 768px) {
  .v-navigation-drawer {
    display: none !important;
  }

  .v-main {
    padding-left: 0 !important;
  }
}

.theme--dark.v-navigation-drawer.job-updates-feed {
  background-color: rgba(17, 24, 39, .8);
}

.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 10px hsla(0, 0%, 0%, .03);
}

.theme--dark .drawer-content {
  box-shadow: 0 0 10px hsla(0, 0%, 100%, .02);
  background: linear-gradient(145deg, #1e293b, #0f172a);
}

.modern-drawer-header {
  padding: 0px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 10px 0px 10px;
}

.modern-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  background: rgba(96, 165, 250, 0.1);
  color: #004bfb;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
}

.button-icon {
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
}

.button-text {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Projects Section */
.projects-container {
  padding: 16px;
}

.section-title {
  margin-bottom: 8px;
}

.projects-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.projects-list.mini-mode {
  align-items: center;
}

.project-card {
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.7), rgba(59, 130, 246, 0.9));
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  width: 100%;
}

.project-card.mini-mode {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.project-card.active {
  border-color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.project-card-content {
  display: flex;
  align-items: center;
  color: white;
  width: 100%;
}

.mini-mode .project-card-content {
  justify-content: center;
}

.project-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.mini-mode .project-icon {
  margin-right: 0;
}

.project-details {
  flex: 1;
}

.project-name {
  font-weight: 500;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-id {
  font-size: 0.8rem;
}

.project-description {
  font-size: 0.75rem;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
  margin-top: 2px;
}

/* Metrics Section */
.metrics-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.metric-card {
  background: linear-gradient(145deg, rgba(96, 165, 250, 0.05), rgba(147, 197, 253, 0.1));
  border: 1px solid rgba(96, 165, 250, 0.15);
  border-radius: 12px;
  padding: 12px;
  transition: all 0.3s ease;
}

.metric-card:hover {
  transform: translateX(4px);
  background: linear-gradient(145deg, rgba(96, 165, 250, 0.1), rgba(147, 197, 253, 0.15));
  border-color: rgba(96, 165, 250, 0.25);
}

.metric-card.highlight {
  background: linear-gradient(145deg, rgba(34, 197, 94, 0.05), rgba(74, 222, 128, 0.1));
  border-color: rgba(34, 197, 94, 0.2);
}

.metric-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.metric-value {
  display: flex;
  align-items: center;
  gap: 12px;
}

.number {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--v-error-base);
  min-width: 32px;
  text-align: center;
}

.number.success {
  color: var(--v-success-base);
}

.label {
  font-size: 0.875rem;
}

/* Empty State */
.empty-state-jobs {
  padding: 24px 0px;
}

.empty-state-jobs-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.icon-container {
  background: rgba(96, 165, 250, 0.1);
  border-radius: 50%;
  padding: 16px;
  margin-bottom: 8px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.empty-description {
  font-size: 0.875rem;
  line-height: 1.5;
}

.action-list {
  list-style: none;
  padding: 0;
  margin: 12px 0;
}

.action-list li {
  margin: 8px 0;
  padding-left: 24px;
  position: relative;
}

.action-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--v-primary-base);
}

/* Job Items */
.job-item {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  padding: 0px 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  border: 1px solid transparent;
}

.job-item-content {
  width: 100%;
  display: flex;
  align-items: center;
}

.job-type {
  font-weight: bold;
  width: 100%;
  margin-bottom: 5px;
  color: #004bfb;
}

.job-name {
  flex: 1;
  margin-right: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-indicator {
  display: flex;
  align-items: center;
  margin-left: -15px;
}

/* Planet tooltip styles */
.planet-tooltip {
  max-width: 300px;
  background-color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.file-list-container {
  max-height: 200px;
  overflow-y: auto;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 8px;
  margin-top: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 0.8rem;
}

.file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-files {
  font-size: 0.8rem;
  font-style: italic;
  padding: 4px 0;
}

/* Tooltip styles */
.job-tooltip {
  backdrop-filter: blur(10px);
}

.tooltip-details {
  margin-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 0.85rem;
}

/* Mini Mode Adjustments */
.mini-mode .metric-content {
  justify-content: center;
}

.mini-mode .number {
  font-size: 1rem;
}

/* Add modern styles while keeping original functionality */

/* Modernize the base drawer */
.job-updates-feed.modern-feed {
  backdrop-filter: blur(12px);
  background-color: rgba(255, 255, 255, 0.9) !important;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.08);
}

.theme--dark.job-updates-feed.modern-feed {
  background-color: rgba(17, 24, 39, .95) !important;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.theme--dark.modern-feed {
  background-color: rgba(17, 24, 39, .95) !important;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

/* Glass morphism buttons - enhanced modern-button */
.modern-button.glass-button {
  backdrop-filter: blur(8px);
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.15), rgba(96, 165, 250, 0.05)) !important;
  border: 1px solid rgba(96, 165, 250, 0.2) !important;
  box-shadow: 0 2px 8px rgba(96, 165, 250, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-button:hover {
  background: rgba(96, 165, 250, 0.2);
  transform: translateY(-1px);
}

.glass-button:hover {
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.25), rgba(96, 165, 250, 0.15)) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(96, 165, 250, 0.2);
}

.refreshing .button-icon {
  transform: rotate(180deg);
}

.glass-button.refreshing {
  background: linear-gradient(45deg, rgba(96, 165, 250, 0.2), rgba(147, 197, 253, 0.2)) !important;
}

/* Animate the refresh icon */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Smooth icon transitions */
.icon-animate {
  transition: transform 0.3s ease;
}

/* Modern title styles - enhance existing section-title */
.section-title.modern-title {
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.5);
}

.theme--dark .section-title.modern-title {
  color: rgba(255, 255, 255, 0.5);
}

/* Enhance project cards - add to existing styles */
.project-card.modern-card {
  background: var(--project-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.project-card.modern-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.project-card.modern-card:hover::before {
  opacity: 1;
}

.project-card.modern-card:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.project-card.modern-card.active {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8), 0 12px 32px rgba(0, 0, 0, 0.2);
}

/* Modern project icon - enhance existing project-icon */
.project-icon-wrapper {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border-radius: 10px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.project-card:hover .project-icon-wrapper {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.1);
}

/* Modern metrics - enhance existing metric-card */
.metric-card.modern-metric {
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(96, 165, 250, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.metric-card.modern-metric:hover {
  transform: translateY(-2px) translateX(2px);
  box-shadow: 0 6px 20px rgba(96, 165, 250, 0.15);
}

.metric-number {
  font-weight: 700;
  background: linear-gradient(135deg, #3B82F6, #60A5FA);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.metric-number.success {
  background: linear-gradient(135deg, #10B981, #34D399);
}

/* Modern spinner - enhance existing spinner */
.spinner.modern-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--v-error-base);
  border-radius: 50%;
  animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

/* Modern empty state - enhance existing styles */
.empty-state-jobs.modern-empty {
  padding: 32px 16px;
}

.icon-container.modern-icon-container {
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.1), rgba(147, 197, 253, 0.15));
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(96, 165, 250, 0.1);
}

.empty-icon.modern-empty-icon {
  color: #3B82F6 !important;
}

.modern-pro-button {
  background: linear-gradient(135deg, #3B82F6, #2563EB) !important;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.modern-pro-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4) !important;
}

/* Modern job items - enhance existing job-item */
.job-item.modern-job-item:hover {
  background: linear-gradient(to right, rgba(230,240,255,0.1) 0%, rgba(230,240,255,0.3) 50%, rgba(230,240,255,0.1) 100%);
}

.job-item.modern-job-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--job-accent-color, #3B82F6);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.job-item.modern-job-item:hover {
  background: linear-gradient(to right, rgba(96, 165, 250, 0.05), rgba(96, 165, 250, 0.1), rgba(96, 165, 250, 0.05));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transform: translateX(4px);
}

.job-item.modern-job-item:hover::before {
  transform: scaleY(1);
}

/* Modern avatars - enhance existing outer-avatar */
.outer-avatar.modern-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.outer-avatar.modern-avatar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.job-item:hover .outer-avatar.modern-avatar::after {
  opacity: 1;
}

/* Modern status indicators - enhance existing status-indicator */
.status-indicator.modern-status {
  gap: 6px;
}

.status-indicator.modern-status .v-progress-circular {
  transition: transform 0.3s ease;
}

.job-item:hover .status-indicator.modern-status .v-progress-circular {
  transform: scale(1.1);
}

/* Modern tooltips - enhance existing tooltips */
.planet-tooltip.modern-tooltip,
.job-tooltip.modern-tooltip {
  backdrop-filter: blur(12px) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .planet-tooltip.modern-tooltip,
.theme--dark .job-tooltip.modern-tooltip {
  background: rgba(30, 41, 59, 0.95) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Modern chips - enhance existing chips */
.modern-chip {
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
}

.job-item:hover .modern-chip {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Modern metric number - enhance number class */
.number.metric-number {
  font-weight: 700;
  background: linear-gradient(135deg, #3B82F6, #60A5FA);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.number.metric-number.success {
  background: linear-gradient(135deg, #10B981, #34D399);
}

/* Enhanced job type specific styling */
.job-item[data-job-type="File Submission"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #10B981, #3B82F6) !important;
}

.job-item[data-job-type="File Submission"][data-is-image="true"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #10B981, #3B82F6) !important;
}

.job-item[data-job-type="File Submission"][data-is-image="false"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #F43F5E, #EC4899) !important;
}

.job-item[data-job-type="Text Submission"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #3B82F6, #06B6D4) !important;
}

.job-item[data-job-type="Web Crawl"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #8B5CF6, #6366F1) !important;
}

.job-item[data-job-type="YouTube Transcript"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #FF0000, #DC2626) !important;
}

.job-item[data-job-type="Chat Message"] .outer-avatar.modern-avatar {
  background: linear-gradient(135deg, #F59E0B, #EF4444) !important;
}

/* Enhanced icons with modern styling */
.outer-avatar.modern-avatar .inner-avatar {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(10px);
}

.outer-avatar.modern-avatar .v-icon {
  font-size: 22px !important;
  color: white !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Job type badges */
.job-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 4px;
}

.job-type-badge[data-type="file"] {
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
}

.job-type-badge[data-type="image"] {
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
}

.job-type-badge[data-type="text"] {
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
}

.job-type-badge[data-type="web"] {
  background: rgba(139, 92, 246, 0.1);
  color: #8B5CF6;
}

.job-type-badge[data-type="youtube"] {
  background: rgba(255, 0, 0, 0.1);
  color: #FF0000;
}

.job-type-badge[data-type="chat"] {
  background: rgba(245, 158, 11, 0.1);
  color: #F59E0B;
}

/* Enhancement animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Smooth icon transitions */
.icon-animate {
  transition: transform 0.3s ease;
}

/* Smooth scroll */
.feed-content {
  scroll-behavior: smooth;
}

/* Modern scrollbar */
.feed-content::-webkit-scrollbar,
.jobs-container::-webkit-scrollbar {
  width: 6px;
}

.feed-content::-webkit-scrollbar-track,
.jobs-container::-webkit-scrollbar-track {
  background: transparent;
}

.feed-content::-webkit-scrollbar-thumb,
.jobs-container::-webkit-scrollbar-thumb {
  background: rgba(96, 165, 250, 0.2);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.feed-content::-webkit-scrollbar-thumb:hover,
.jobs-container::-webkit-scrollbar-thumb:hover {
  background: rgba(96, 165, 250, 0.4);
}

.theme--dark .feed-content::-webkit-scrollbar-thumb,
.theme--dark .jobs-container::-webkit-scrollbar-thumb {
  background: rgba(96, 165, 250, 0.3);
}

.theme--dark .feed-content::-webkit-scrollbar-thumb:hover,
.theme--dark .jobs-container::-webkit-scrollbar-thumb:hover {
  background: rgba(96, 165, 250, 0.5);
}

/* Modern status indicators */
.modern-status {
  gap: 6px;
}

.modern-status .v-progress-circular {
  transition: transform 0.3s ease;
}

.job-item:hover .modern-status .v-progress-circular {
  transform: scale(1.1);
}

/* Modern tooltips */
.modern-tooltip {
  backdrop-filter: blur(12px) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .modern-tooltip {
  background: rgba(30, 41, 59, 0.95) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Modern chips */
.modern-chip {
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
}

.job-item:hover .modern-chip {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Smooth scroll */
.feed-content {
  scroll-behavior: smooth;
}

/* Modern scrollbar */
.feed-content::-webkit-scrollbar {
  width: 6px;
}

.feed-content::-webkit-scrollbar-track {
  background: transparent;
}

.feed-content::-webkit-scrollbar-thumb {
  background: rgba(96, 165, 250, 0.2);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.feed-content::-webkit-scrollbar-thumb:hover {
  background: rgba(96, 165, 250, 0.4);
}

.theme--dark .feed-content::-webkit-scrollbar-thumb {
  background: rgba(96, 165, 250, 0.3);
}

.theme--dark .feed-content::-webkit-scrollbar-thumb:hover {
  background: rgba(96, 165, 250, 0.5);
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .job-updates-feed {
    display: none !important;
  }
}
</style>
